import { Component } from '@theme/component';

const LEAFLET_VERSION = '1.9.4';
const LEAFLET_BASE = `https://cdnjs.cloudflare.com/ajax/libs/leaflet/${LEAFLET_VERSION}`;
const LEAFLET_JS_INTEGRITY =
  'sha512-BwHfrr4c9kmRkLw6iXFdzcdWV/PGkVgiIyIWLLlTSXzWQzxuSg4DiQUCpauz/EWjgk5TYQqX/kvn9pG1NpYfqg==';
const LEAFLET_CSS_INTEGRITY =
  'sha512-Zcn6bjR/8RZbLEpLIeOwNtzREBAJnUKESxces60Mpoj+2okopSAcSUIUOseddDm0cxnGQzxIR7vJgsLZbdLE3w==';

const SWEDEN_BOUNDS = [
  [55.3, 11.0],
  [69.1, 24.2],
];

/** @type {Promise<any> | undefined} */
let leafletPromise;

function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);

  leafletPromise ??= new Promise((resolve, reject) => {
    const stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = `${LEAFLET_BASE}/leaflet.css`;
    stylesheet.integrity = LEAFLET_CSS_INTEGRITY;
    stylesheet.crossOrigin = 'anonymous';
    document.head.append(stylesheet);

    const script = document.createElement('script');
    script.src = `${LEAFLET_BASE}/leaflet.js`;
    script.integrity = LEAFLET_JS_INTEGRITY;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.head.append(script);
  });

  return leafletPromise;
}

/**
 * Map of Swebad showrooms, rendered with Leaflet and OpenStreetMap tiles,
 * with an optional list of showrooms linked to the markers.
 *
 * @typedef {object} ShowroomMapRefs
 * @property {HTMLElement} [map] - The element Leaflet renders into
 * @property {HTMLScriptElement} [data] - JSON list of showrooms
 * @property {HTMLElement[]} [items] - List items, keyed by `data-showroom`
 *
 * @extends {Component<ShowroomMapRefs>}
 */
class WinterShowroomMap extends Component {
  /** @type {Map<string, any>} */
  markers = new Map();

  connectedCallback() {
    super.connectedCallback();

    if (!this.refs.map) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.observer?.disconnect();
        loadLeaflet().then((L) => {
          if (this.isConnected && !this.leafletMap) this.render(L);
        });
      },
      { rootMargin: '200px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.observer?.disconnect();
    this.leafletMap?.remove();
    this.leafletMap = undefined;
    this.markers.clear();
  }

  /**
   * @param {any} L
   */
  render(L) {
    const showrooms = JSON.parse(this.refs.data.textContent || '[]');
    const linkLabel = this.dataset.linkLabel || '';

    this.leafletMap = L.map(this.refs.map, { scrollWheelZoom: false, zoomSnap: 0.5 });
    this.leafletMap.fitBounds(SWEDEN_BOUNDS);

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.leafletMap);

    const icon = L.divIcon({
      className: 'winter-showroom-map__marker',
      iconSize: [28, 36],
      iconAnchor: [14, 36],
      popupAnchor: [0, -32],
      html: '<svg viewBox="0 0 28 36" aria-hidden="true"><path d="M14 0C6.3 0 0 6.2 0 13.9 0 24.3 14 36 14 36s14-11.7 14-22.1C28 6.2 21.7 0 14 0z"/><circle cx="14" cy="14" r="5"/></svg>',
    });

    for (const showroom of showrooms) {
      const marker = L.marker([showroom.lat, showroom.lng], { icon, title: showroom.name, alt: showroom.name })
        .addTo(this.leafletMap)
        .bindPopup(this.popupContent(showroom, linkLabel))
        .on('popupopen', () => this.setActiveItem(showroom.id))
        .on('popupclose', () => this.setActiveItem(null));

      this.markers.set(showroom.id, marker);
    }

    if (showrooms.length === 1) {
      this.leafletMap.setView([showrooms[0].lat, showrooms[0].lng], 14);
    }

    for (const item of this.refs.items ?? []) {
      const locateButton = item.querySelector('.winter-showroom-map__item-locate');
      if (locateButton instanceof HTMLElement) {
        locateButton.hidden = !this.markers.has(item.dataset.showroom ?? '');
      }
    }
  }

  /**
   * @param {Event} event
   */
  showOnMap(event) {
    const item = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-showroom]') : null
    );
    const marker = this.markers.get(item?.dataset.showroom ?? '');
    if (!marker || !this.leafletMap || !this.refs.map) return;

    const mapRect = this.refs.map.getBoundingClientRect();
    if (mapRect.top < 0 || mapRect.bottom > window.innerHeight) {
      this.refs.map.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    this.leafletMap.flyTo(marker.getLatLng(), Math.max(this.leafletMap.getZoom(), 9));
    this.leafletMap.once('moveend', () => marker.openPopup());
  }

  /**
   * @param {string | null} id
   */
  setActiveItem(id) {
    for (const item of this.refs.items ?? []) {
      item.classList.toggle('is-active', item.dataset.showroom === id);
    }
  }

  /**
   * @param {{ name: string, address: string, url: string }} showroom
   * @param {string} linkLabel
   */
  popupContent(showroom, linkLabel) {
    const content = document.createElement('div');
    content.className = 'winter-showroom-map__popup';

    const name = document.createElement('strong');
    name.textContent = showroom.name;
    content.append(name);

    if (showroom.address) {
      const address = document.createElement('p');
      address.textContent = showroom.address;
      content.append(address);
    }

    if (showroom.url && linkLabel) {
      const link = document.createElement('a');
      link.href = showroom.url;
      link.textContent = linkLabel;
      content.append(link);
    }

    return content;
  }
}

if (!customElements.get('winter-showroom-map')) {
  customElements.define('winter-showroom-map', WinterShowroomMap);
}
